import { Fragment } from 'react';

import { copyText, fmt } from '../bamsShortsUtils';
import { InventoryCell } from '../InventoryCell';
import { LocTable } from '../LocTable';

// Priority band for a chase row, derived from its recipe-weighted score.
const band = (r) => (r.score >= 0.45 ? ['P1', '#96261C'] : r.score >= 0.18 ? ['P2', '#D97A38'] : ['P3', '#5B7183']);

// Ranked recovery chase list per facility/brand, with live inventory per SKU and an
// on-demand location drill-down. `invFor(brand, facility, sku)` resolves the inventory
// badge; `toggleLoc` lazily loads location rows into `locData`.
export function RecoveryChasePanel({ chase, invFor, locData, locOpen, toggleLoc }) {
    const copyChase = (group) => {
        const [brand, fc] = group.k.split('|');
        const txt = `${fc} ${brand.replace('_', ' ')} recovery chase — week ${chase.wk} (${fmt(group.d)} orders short)\n${group.rows
            .map((r, i) => {
                const iv = invFor(brand, fc, r.sku);
                const invStr = iv
                    ? iv.ats > 0
                        ? `${fmt(iv.ats)} ATS on hand`
                        : iv.qc > 0
                          ? `NO ATS — ${fmt(iv.qc)} in QC hold`
                          : iv.unusable > 0
                            ? `NO ATS — ${fmt(iv.unusable)} in unusable locations`
                            : 'NO INVENTORY in building'
                    : 'inventory unknown';
                return `${i + 1}. ${r.name} (SKU ${r.sku}) [${r.catO.lab}] — ${fmt(r.orders)} orders (${(
                    100 * r.share
                ).toFixed(0)}% of short orders) — ${invStr}`;
            })
            .join('\n')}`;
        copyText(txt);
    };

    return (
        <div className="panel">
            <p className="ptitle">Recovery chase list</p>
            <p className="pnote">
                What to recover first in week {chase.wk}, ranked by orders short weighted toward the items that make a
                box unshippable rather than merely incomplete. Categories are inferred from item names, so spot-check
                anything odd.
            </p>
            {chase.network.length ? (
                <div className="gap">
                    <b>Chase with the vendor, not the floor.</b> Short at {chase.netmin}+ facility-brands this week:{' '}
                    {chase.network.map((x) => (
                        <span
                            key={x.sku}
                            style={{
                                display: 'inline-block',
                                background: 'var(--panel)',
                                border: '1px solid var(--rule)',
                                borderRadius: 2,
                                padding: '3px 8px',
                                margin: '5px 5px 0 0',
                                fontSize: 12,
                            }}
                        >
                            {x.name}{' '}
                            <span className="sm">
                                {x.n} · {fmt(x.orders)} orders
                            </span>
                        </span>
                    ))}
                </div>
            ) : null}
            <div className="grid2">
                {chase.built.map((group) => {
                    const [brand, fc] = group.k.split('|');
                    const p1 = group.rows.filter((r) => band(r)[0] === 'P1').length;
                    return (
                        <div className="chasefc" key={group.k}>
                            <div className="chasehead">
                                <span>
                                    <b>{fc}</b>{' '}
                                    <span className="sm">
                                        {brand.replace('_', ' ')} · {fmt(group.d)} orders short
                                    </span>
                                </span>
                                <button type="button" className="ghost tiny" onClick={() => copyChase(group)}>
                                    Copy list
                                </button>
                            </div>
                            {p1 ? (
                                <div className="p1note">
                                    {p1} P1 item{p1 > 1 ? 's' : ''} — recover before the next wave.
                                </div>
                            ) : null}
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: 30 }} />
                                        <th className="item">Item</th>
                                        <th className="n">Orders</th>
                                        <th className="n">% short</th>
                                        <th className="d">Δ</th>
                                        <th className="inv">Inventory</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.rows.map((r) => {
                                        const [bl, bc] = band(r);
                                        const isNet = chase.fcCount.get(r.sku) >= chase.netmin;
                                        const locKey = `${brand}|${fc}|${r.sku}`;
                                        const open = locOpen === locKey;
                                        const ld = locData[locKey];
                                        return (
                                            <Fragment key={r.sku}>
                                                <tr>
                                                    <td>
                                                        <span className="band" style={{ background: bc }}>
                                                            {bl}
                                                        </span>
                                                    </td>
                                                    <td className="item">
                                                        <span className="cat" style={{ color: r.catO.col }}>
                                                            {r.catO.lab}
                                                        </span>
                                                        {r.name || ''}
                                                        <span className="skunum">{r.sku}</span>
                                                        {isNet ? <span className="scope">network</span> : null}
                                                    </td>
                                                    <td className="n">{fmt(r.orders)}</td>
                                                    <td className="n">{(100 * r.share).toFixed(0)}%</td>
                                                    <td
                                                        className={`d ${
                                                            r.trend === null
                                                                ? ''
                                                                : r.trend > 0.02
                                                                  ? 'pos'
                                                                  : r.trend < -0.02
                                                                    ? 'neg'
                                                                    : ''
                                                        }`}
                                                    >
                                                        {r.trend === null
                                                            ? '—'
                                                            : `${r.trend >= 0 ? '+' : ''}${(100 * r.trend).toFixed(0)}`}
                                                    </td>
                                                    <td
                                                        className={`inv${open ? ' open' : ''}`}
                                                        data-loc
                                                        title="Click to see where this is in the building"
                                                        onClick={() => toggleLoc(fc, r.sku, r.facilityId, brand)}
                                                    >
                                                        <InventoryCell v={invFor(brand, fc, r.sku)} />
                                                    </td>
                                                </tr>
                                                {open ? (
                                                    <tr className="locrow">
                                                        <td colSpan={6}>
                                                            {ld && ld.loading ? (
                                                                <span className="sm">
                                                                    <span className="spin" />
                                                                    Looking up locations…
                                                                </span>
                                                            ) : ld && ld.error ? (
                                                                <span className="sm" style={{ color: 'var(--s8)' }}>
                                                                    {ld.error}
                                                                </span>
                                                            ) : ld && ld.rows ? (
                                                                <LocTable rows={ld.rows} />
                                                            ) : null}
                                                        </td>
                                                    </tr>
                                                ) : null}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
            <p className="foot">
                Ranked by orders short weighted by how badly the item breaks the recipe — protein 3×, base carb 2×,
                fresh 1.3×, pantry 1×. Δ is the change in share of that facility&apos;s short orders versus the prior
                week. Inventory is live ATS units in usable locations;{' '}
                <span style={{ color: 'var(--s6)', fontWeight: 600 }}>QC</span> means none available to sell but units
                on QC hold, <span style={{ color: 'var(--s6)', fontWeight: 600 }}>LOC</span> means units stuck in
                unusable locations, and <span style={{ color: 'var(--muted)' }}>none</span> means nothing in the
                building. Click any inventory figure to see which locations it sits in.
            </p>
        </div>
    );
}
